/**
 * Shared dispatcher for document/window-level listeners.
 *
 * Every ChartManager needs a handful of GLOBAL listeners (Alt/Shift key
 * trackers, drawing hotkeys, Escape draw-cancel, outside-click deselect).
 * Registered directly, a 9-chart grid runs ~63 document/window handlers and
 * every keydown/mousedown fans through all of them. The hub keeps exactly ONE
 * real listener per (target, type, phase) — installed when the first handler
 * registers, removed when the last one leaves — and fans events out to the
 * per-instance handlers itself. Same pattern as
 * `registerLabelVisibilityResolver` in riskRewardOverlay.ts.
 *
 * Dispatch semantics (matching N separate same-node listeners):
 * - Handlers run in registration order and receive the real event.
 * - `stopPropagation()` in one handler does NOT skip its siblings — same-node
 *   listeners all fire regardless, exactly as before the hub.
 * - A handler unregistered mid-dispatch (by itself or an earlier handler)
 *   stops firing immediately, mirroring DOM `removeEventListener` semantics.
 * - KNOWN DIFFERENCE: `stopImmediatePropagation()` inside a hub handler can no
 *   longer suppress sibling hub handlers (the flag is not readable from JS).
 *   No migrated handler calls it — keep it that way, or route that listener
 *   directly instead of through the hub.
 *
 * SSR-safe: `document`/`window` are only touched inside `hubAdd`, never at
 * module scope.
 */

type HubTarget = 'document' | 'window'

interface HubEntry {
  handlers: Set<(e: Event) => void>
  /** The single REAL listener installed on document/window for this key. */
  dispatch: (e: Event) => void
}

const entries = new Map<string, HubEntry>()

function resolveTarget(target: HubTarget): EventTarget {
  return target === 'document' ? document : window
}

/**
 * Register `handler` for a global event. Installs the real listener on first
 * registration for the (target, type, phase) key; returns an idempotent
 * unregister that removes the real listener when the last handler leaves.
 */
export function hubAdd<E extends Event>(
  target: HubTarget,
  type: string,
  handler: (e: E) => void,
  options?: { capture?: boolean },
): () => void {
  const capture = options?.capture === true
  const key = `${target}|${type}|${capture ? 'capture' : 'bubble'}`

  let entry = entries.get(key)
  if (!entry) {
    const handlers = new Set<(e: Event) => void>()
    const dispatch = (e: Event) => {
      // Snapshot so (un)registrations mid-dispatch cannot corrupt iteration;
      // the live-Set check skips handlers an earlier handler just removed.
      for (const fn of [...handlers]) {
        if (handlers.has(fn)) fn(e)
      }
    }
    entry = { handlers, dispatch }
    entries.set(key, entry)
    resolveTarget(target).addEventListener(type, dispatch, capture)
  }

  const registered = entry
  const fn = handler as (e: Event) => void
  registered.handlers.add(fn)

  let active = true
  return () => {
    if (!active) return
    active = false
    registered.handlers.delete(fn)
    if (registered.handlers.size === 0 && entries.get(key) === registered) {
      entries.delete(key)
      resolveTarget(target).removeEventListener(type, registered.dispatch, capture)
    }
  }
}
